// 기호 연산 — 처음 보는 기호의 예시(3 ◎ 2 = 12 …)에서 숨은 계산식을 찾아 ? 를 채운다.
// 결과에서 공식을 거꾸로 알아내는 연습 (수수료·점수·게임 공식이 '어떻게 계산됐나'와 같은 사고).
// 공정성: 후보 규칙 전부를 예시에 대 보고, 예시에 맞는 규칙이 여럿이면 ? 의 답이 모두 같아질 때까지 예시를 늘린다.
import { makeRng, pick, shuffle } from './rng';

export interface OpRule {
  key: string;
  level: number;
  /** 'a ○ b = …' 의 오른쪽. 화면에서 ○ 자리에 그 판의 기호가 들어간다 */
  text: string;
  /** 답 확인·모호성 검사용 계산 (음수·소수도 그대로 낸다) */
  f: (a: number, b: number) => number;
  /** 이 규칙으로 문제를 낼 때 쓸 수 있는 a, b */
  valid?: (a: number, b: number) => boolean;
}

export interface OpPuzzle {
  symbol: string;
  examples: { a: number; b: number; result: number }[];
  query: { a: number; b: number };
  answer: number;
  rule: string;
  ruleKey: string;
  explain: string;
  limitSec: number;
}

export const OP_COUNT = 6;
const SYMBOLS = ['◎', '★', '◆', '▲', '●', '♥', '■', '♣'];

/** 수를 이어 붙인다 (3, 2 → 32). 음수·소수는 이어 붙일 수 없다 */
const cat = (...xs: number[]): number => (xs.some((x) => x < 0 || !Number.isInteger(x)) ? NaN : Number(xs.join('')));
const aGtB = (a: number, b: number) => a > b;

export const OP_RULES: OpRule[] = [
  ...[1, 2, 3, 4, 5].map((c): OpRule => ({ key: `addc${c}`, level: 1, text: `a + b + ${c}`, f: (a, b) => a + b + c })),
  { key: 'mul', level: 1, text: 'a × b', f: (a, b) => a * b },
  ...[1, 2, 3, 4, 5].map((c): OpRule => ({ key: `mulc${c}`, level: 1, text: `a × b + ${c}`, f: (a, b) => a * b + c })),
  { key: 'dsum', level: 1, text: '(a + b) × 2', f: (a, b) => (a + b) * 2 },
  { key: 'lin', level: 1, text: 'a × 2 + b', f: (a, b) => a * 2 + b },
  { key: 'mulsuba', level: 2, text: 'a × b − a', f: (a, b) => a * b - a },
  { key: 'mulb1', level: 2, text: 'a × (b + 1)', f: (a, b) => a * (b + 1) },
  { key: 'sqplusb', level: 2, text: 'a² + b', f: (a, b) => a * a + b },
  { key: 'mul2', level: 2, text: 'a × b × 2', f: (a, b) => a * b * 2 },
  { key: 'linb', level: 2, text: 'a + b × 2', f: (a, b) => a + b * 2 },
  { key: 'mulps', level: 3, text: 'a × b + a + b', f: (a, b) => a * b + a + b },
  { key: 'cat', level: 3, text: 'a 와 b 를 이어 붙인 수 (3, 2 → 32)', f: (a, b) => cat(a, b) },
  { key: 'catr', level: 3, text: 'b 와 a 를 이어 붙인 수 (3, 2 → 23)', f: (a, b) => cat(b, a) },
  ...[2, 3, 4, 5].map((c): OpRule => ({ key: `diffc${c}`, level: 3, text: `(a − b) × ${c}`, f: (a, b) => (a - b) * c, valid: aGtB })),
  { key: 'suma', level: 4, text: '(a + b) × a', f: (a, b) => (a + b) * a },
  { key: 'sqdiff', level: 4, text: 'a² − b²', f: (a, b) => a * a - b * b, valid: aGtB },
  { key: 'sqsum', level: 4, text: 'a² + b²', f: (a, b) => a * a + b * b },
  { key: 'mulmsum', level: 4, text: 'a × b − (a + b)', f: (a, b) => a * b - (a + b), valid: (a, b) => a >= 2 && b >= 2 },
  { key: 'catsm', level: 5, text: '(a + b) 와 (a × b) 를 이어 붙인 수', f: (a, b) => cat(a + b, a * b) },
  { key: 'catds', level: 5, text: '(a − b) 와 (a + b) 를 이어 붙인 수', f: (a, b) => cat(a - b, a + b), valid: aGtB },
  { key: 'sumsq', level: 5, text: '(a + b)²', f: (a, b) => (a + b) ** 2 },
  { key: 'catms', level: 6, text: '(a × b) 와 (a + b) 를 이어 붙인 수', f: (a, b) => cat(a * b, a + b) },
  { key: 'pow', level: 6, text: 'a 의 b 제곱 (a^b)', f: (a, b) => a ** b, valid: (a, b) => a >= 2 && a <= 6 && b <= 4 },
  { key: 'sumb', level: 6, text: '(a + b) × b', f: (a, b) => (a + b) * b },
  { key: 'catsq', level: 7, text: 'a² 과 b² 을 이어 붙인 수', f: (a, b) => cat(a * a, b * b) },
  { key: 'sqminb', level: 7, text: 'a² − b', f: (a, b) => a * a - b },
  { key: 'catmd', level: 7, text: '(a × b) 와 (a − b) 를 이어 붙인 수', f: (a, b) => cat(a * b, a - b), valid: aGtB },
  { key: 'sqtimesb', level: 8, text: 'a² × b', f: (a, b) => a * a * b },
  { key: 'mulsq', level: 8, text: '(a × b)²', f: (a, b) => (a * b) ** 2 },
  { key: 'cat3', level: 8, text: 'a, b, (a + b) 를 차례로 이어 붙인 수', f: (a, b) => cat(a, b, a + b) },
  { key: 'poly1', level: 9, text: 'a × b + a − b', f: (a, b) => a * b + a - b },
  { key: 'poly2', level: 9, text: 'a² + a × b + b²', f: (a, b) => a * a + a * b + b * b },
  { key: 'summul', level: 9, text: '(a + b) × (a × b)', f: (a, b) => (a + b) * a * b },
  { key: 'range', level: 10, text: 'a 부터 b 까지 모두 더한 수', f: (a, b) => (a <= b ? ((a + b) * (b - a + 1)) / 2 : NaN), valid: (a, b) => a < b },
  { key: 'powminb', level: 10, text: 'a^b − b', f: (a, b) => a ** b - b, valid: (a, b) => a >= 2 && a <= 6 && b >= 2 && b <= 4 },
  { key: 'muldiff', level: 10, text: 'a × b × (a − b)', f: (a, b) => a * b * (a - b), valid: aGtB },
];

const usable = (r: OpRule, a: number, b: number) => {
  if (r.valid && !r.valid(a, b)) return false;
  const v = r.f(a, b);
  return Number.isInteger(v) && v >= 0 && v <= 99999;
};

/** 예시에 맞는 모든 후보 규칙이 ? 에 내는 답들 */
export function possibleAnswers(examples: { a: number; b: number; result: number }[], query: { a: number; b: number }): Set<number> {
  const out = new Set<number>();
  for (const r of OP_RULES) {
    if (examples.every((e) => r.f(e.a, e.b) === e.result)) out.add(r.f(query.a, query.b));
  }
  return out;
}

export function makeOpRound(level: number, seed: number): OpPuzzle[] {
  const rng = makeRng(seed);
  const lv = Math.min(10, Math.max(1, level));
  const pool = OP_RULES.filter((r) => r.level <= lv && r.level >= lv - 2);
  const newest = pool.filter((r) => r.level === Math.max(...pool.map((x) => x.level)));
  const limitSec = lv <= 3 ? 45 : lv <= 6 ? 60 : 90;
  const symbols = shuffle(rng, SYMBOLS);
  const out: OpPuzzle[] = [];
  for (let guard = 0; out.length < OP_COUNT && guard < 500; guard++) {
    const rule = rng() < 0.5 ? pick(rng, newest) : pick(rng, pool);
    if (out.some((p) => p.ruleKey === rule.key)) continue; // 한 판 안에서 같은 규칙은 한 번
    const pairs: { a: number; b: number }[] = [];
    for (let a = 1; a <= 9; a++) for (let b = 1; b <= 9; b++) if (usable(rule, a, b)) pairs.push({ a, b });
    if (pairs.length < 6) continue;
    const [query, ...rest] = shuffle(rng, pairs);
    const examples: OpPuzzle['examples'] = [];
    for (const p of rest) {
      if (examples.length >= 3 && possibleAnswers(examples, query).size === 1) break;
      if (examples.length >= 5) break;
      examples.push({ ...p, result: rule.f(p.a, p.b) });
    }
    if (possibleAnswers(examples, query).size !== 1) continue; // 예시 5개로도 답이 하나로 안 좁혀지면 다른 문제
    const symbol = symbols[out.length % symbols.length];
    const answer = rule.f(query.a, query.b);
    const ruleText = `a ${symbol} b = ${rule.text}`;
    out.push({
      symbol, examples, query, answer, rule: ruleText, ruleKey: rule.key, limitSec,
      explain: `규칙: ${ruleText} → ${query.a} ${symbol} ${query.b} = ${answer}`,
    });
  }
  return out;
}
